import { useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Zap, Sparkles, Calculator } from "lucide-react";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

interface PrecioEstimadoWidgetProps {
  cityName?: string;
  serviceType?: "mudanza" | "profunda" | "estandar";
  className?: string;
}

const BASE_PRICES = {
  mudanza:   { base: 249, perBed: 45, perBath: 55 },
  profunda:  { base: 279, perBed: 40, perBath: 50 },
  estandar:  { base: 120, perBed: 30, perBath: 40 },
};

const SERVICE_LABELS = {
  mudanza:  "Limpieza de Mudanza",
  profunda: "Limpieza Profunda",
  estandar: "Limpieza Estándar",
};

export const PrecioEstimadoWidget = ({
  cityName = "tu ciudad",
  serviceType = "mudanza",
  className,
}: PrecioEstimadoWidgetProps) => {
  const [bedrooms, setBedrooms] = useState([2]);
  const [bathrooms, setBathrooms] = useState([2]);
  const [selectedService, setSelectedService] = useState<keyof typeof BASE_PRICES>(serviceType);

  const { base, perBed, perBath } = BASE_PRICES[selectedService];
  const price = base + (bedrooms[0] - 1) * perBed + (bathrooms[0] - 1) * perBath;

  return (
    <div className={cn("rounded-3xl border-2 border-primary/20 bg-card shadow-lg overflow-hidden", className)}>
      {/* Header */}
      <div className="bg-primary px-6 py-5 text-primary-foreground">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center">
            <Calculator className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-bold text-lg leading-tight">Precio Estimado al Instante</h3>
            <p className="text-primary-foreground/80 text-sm">{cityName} — Sin visita previa requerida</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Service selector */}
        <div>
          <label className="text-sm font-semibold mb-3 block text-muted-foreground uppercase tracking-wide">Tipo de Servicio</label>
          <div className="grid grid-cols-3 gap-2">
            {(Object.keys(SERVICE_LABELS) as Array<keyof typeof SERVICE_LABELS>).map((key) => (
              <button
                key={key}
                onClick={() => setSelectedService(key)}
                className={cn(
                  "py-2 px-3 rounded-xl text-xs font-semibold border transition-all duration-200",
                  selectedService === key
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-muted/50 text-muted-foreground border-border hover:border-primary/50"
                )}
              >
                {key === "mudanza" ? "🏠 Mudanza" : key === "profunda" ? "✨ Profunda" : "🧹 Estándar"}
              </button>
            ))}
          </div>
        </div>

        {/* Sliders */}
        <div className="space-y-5">
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="text-sm font-semibold">Recámaras</label>
              <span className="text-2xl font-bold text-primary">{bedrooms[0]}</span>
            </div>
            <Slider defaultValue={[2]} max={6} min={1} step={1} value={bedrooms} onValueChange={setBedrooms} className="cursor-pointer" aria-label="Bedrooms" />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>1</span><span>2</span><span>3</span><span>4</span><span>5</span><span>6</span>
            </div>
          </div>
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="text-sm font-semibold">Baños</label>
              <span className="text-2xl font-bold text-primary">{bathrooms[0]}</span>
            </div>
            <Slider defaultValue={[2]} max={5} min={1} step={1} value={bathrooms} onValueChange={setBathrooms} className="cursor-pointer" aria-label="Bathrooms" />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>1</span><span>2</span><span>3</span><span>4</span><span>5</span>
            </div>
          </div>
        </div>

        {/* Price display */}
        <div className="rounded-2xl bg-primary/5 border border-primary/20 p-5 text-center">
          <p className="text-sm text-muted-foreground mb-1 font-medium">
            {SERVICE_LABELS[selectedService]} — {bedrooms[0]} rec, {bathrooms[0]} baños
          </p>
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-sm font-semibold text-muted-foreground">desde</span>
            <span className="text-5xl font-extrabold text-primary">${price}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Precio plano · Sin tarifas por hora · Sin sorpresas</p>
        </div>

        {/* CTAs */}
        <div className="flex flex-col gap-3">
          <Button asChild size="lg" className="w-full h-12 font-bold bg-primary text-primary-foreground hover:bg-primary/90 btn-pulse">
            <a href={BOOKING_URL}>
              <Zap className="h-4 w-4 mr-2" />
              Reservar a ${price}
            </a>
          </Button>
          <Button asChild size="lg" variant="outline" className="w-full h-12 font-bold border-2 border-primary text-primary hover:bg-primary/10">
            <a href={BOOKING_URL}>
              <Sparkles className="h-4 w-4 mr-2" />
              Obtener Precio Exacto
            </a>
          </Button>
        </div>

        <p className="text-center text-xs text-muted-foreground">
          ✅ Garantía del 200% · ✅ Asegurados y verificados · ✅ Productos ecológicos
        </p>
      </div>
    </div>
  );
};
